import React from 'react';
import PropTypes from 'prop-types';
import { Box, List, Grid, Button, ListItem, Typography, ListItemText } from '@material-ui/core';
import clsx from 'clsx';
import i18n from 'i18n-js';
import moment from 'moment';
import { buildUrl } from 'utils';
import { useSelector } from 'react-redux';
import Paginator from 'components/Paginator';
import { useHistory } from 'react-router-dom';
import { useStyles, listTextStyles } from './Reservations.styled';

const ReservationList = ({ params, pagination, reservations, handleChangePage }) => {
  const classes = useStyles();
  const history = useHistory();
  const listClasses = listTextStyles();
  const { localeFormat } = useSelector((state) => state.organizations);
  const guessTZ = moment.tz.guess();

  /**
   * Parse reservation data
   * @param {object} reservation Object with reservation data
   */
  const getReservationData = (reservation = {}) => {
    const reservationInfo = [
      {
        label: i18n.t('reservations.vehicle'),
        value: reservation?.vehicle?.display_name || '--',
      },
      {
        label: i18n.t('reservations.plate'),
        value: reservation?.vehicle?.license_plate || '--',
      },
      {
        label: i18n.t('reservations.location'),
        value: reservation?.start_reserved_location?.name || '--',
      },
    ];

    return reservationInfo;
  };

  const TimezoneComponent = ({ tz = guessTZ }) => (
    <span className={classes.timezone}>{tz ? moment().tz(tz).format('(UTC Z)') : ''}</span>
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      className={classes.listBox}
      data-cy="reservations-widget"
    >
      {reservations?.length ? (
        <>
          <List className={classes.listRoot}>
            {reservations?.map((reservation, index) => {
              return (
                <Box key={index} className={clsx(classes.container, classes?.[reservation.status])}>
                  <Box className={classes.header} width="100%">
                    <Typography color="textPrimary" style={{ lineHeight: 1 }}>
                      <Box
                        fontSize={12}
                        fontWeight="fontWeightMedium"
                        component="span"
                        lineHeight={1}
                      >
                        <Button
                          type="button"
                          variant="text"
                          color="primary"
                          data-cy="view-reservation"
                          disableFocusRipple={true}
                          className={classes.button}
                          onClick={() => history.push(buildUrl(`/reservations/${reservation?.id}`))}
                        >
                          ID: #{reservation?.id}
                        </Button>
                      </Box>
                    </Typography>
                    <Typography
                      style={{ lineHeight: 1 }}
                      className={clsx(classes.status, classes?.[reservation.status])}
                    >
                      <Box
                        fontSize={11}
                        fontWeight="fontWeightMedium"
                        component="span"
                        lineHeight={1}
                      >
                        {i18n.t(`reservations_statuses.${reservation.status}`)}
                      </Box>
                    </Typography>
                  </Box>
                  <Box mt={2} width="100%">
                    <Grid container>
                      <Grid item xs={6}>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                            {i18n.t('reservations.start_reserved')}
                          </Box>
                        </Typography>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} lineHeight={1} component="span">
                            {reservation?.start_reserved
                              ? moment(reservation.start_reserved)
                                  .tz(reservation?.vehicle_group?.time_zone || guessTZ)
                                  .format(`${localeFormat} h:mm A`)
                              : '-'}
                            {reservation?.start_reserved && (
                              <TimezoneComponent tz={reservation?.vehicle_group?.time_zone} />
                            )}
                          </Box>
                        </Typography>
                      </Grid>
                      <Grid item sm={12} md={6}>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                            {i18n.t('reservations.end_reserved')}
                          </Box>
                        </Typography>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} lineHeight={1} component="span">
                            {reservation?.end_reserved
                              ? moment(reservation.end_reserved)
                                  .tz(reservation?.vehicle_group?.time_zone || guessTZ)
                                  .format(`${localeFormat} h:mm A`)
                              : '-'}
                            {reservation?.end_reserved && (
                              <TimezoneComponent tz={reservation?.vehicle_group?.time_zone} />
                            )}
                          </Box>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  {(reservation?.status === 'COMPLETED' ||
                    reservation?.status === 'INPROGRESS') && (
                    <Box mt={2} width="100%">
                      <Grid container>
                        <Grid item xs={6}>
                          <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                            <Box mt={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                              {i18n.t('reservations.start_used')}
                            </Box>
                          </Typography>
                          <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                            <Box mt={2} fontSize={13} lineHeight={1} component="span">
                              {reservation?.start_used
                                ? moment(reservation.start_used)
                                    .tz(reservation?.vehicle_group?.time_zone || guessTZ)
                                    .format(`${localeFormat} h:mm A`)
                                : '-'}
                              {reservation?.start_used && (
                                <TimezoneComponent tz={reservation?.vehicle_group?.time_zone} />
                              )}
                            </Box>
                          </Typography>
                        </Grid>
                        <Grid item sm={12} md={6}>
                          <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                            <Box mt={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                              {i18n.t('reservations.end_used')}
                            </Box>
                          </Typography>
                          <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                            <Box mt={2} fontSize={13} lineHeight={1} component="span">
                              {reservation?.end_used
                                ? moment(reservation.end_used)
                                    .tz(reservation?.vehicle_group?.time_zone || guessTZ)
                                    .format(`${localeFormat} h:mm A`)
                                : '-'}
                              {reservation?.end_used && (
                                <TimezoneComponent tz={reservation?.vehicle_group?.time_zone} />
                              )}
                            </Box>
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                  <List className={classes.list}>
                    {getReservationData(reservation).map((val, index) => (
                      <ListItem key={index} className={classes.listItem}>
                        <ListItemText
                          classes={listClasses}
                          primary={val.label}
                          secondary={
                            <Box
                              display="flex"
                              component="span"
                              flexDirection="column"
                              className={classes.textRight}
                            >
                              <Typography
                                variant="body2"
                                component="span"
                                color="textSecondary"
                                className={classes?.[val.classes]}
                              >
                                {val.value}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })}
          </List>
          <Paginator
            component="div"
            limit={params.limit}
            pagination={pagination}
            rowsPerPageOptions={[3]}
            onPageChange={handleChangePage}
          />
        </>
      ) : (
        <Typography color="textPrimary" style={{ lineHeight: 1 }}>
          <Box fontSize={14} component="span" lineHeight={1}>
            {i18n.t('reservations.no_reservations')}
          </Box>
        </Typography>
      )}
    </Box>
  );
};

ReservationList.propTypes = {
  params: PropTypes.object,
  pagination: PropTypes.object,
  reservations: PropTypes.array,
  handleChangePage: PropTypes.func,
};

ReservationList.defaultProps = {
  params: {
    page: 1,
    limit: 3,
  },
  pagination: {},
  reservations: [],
};

export default ReservationList;
